import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import IconsSend from '@mui/icons-material/Send';

import makeStyles from '@mui/styles/makeStyles';

import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Theme } from '@mui/material/styles';
import { useBatchUpdateInventoryProductMutation } from '../../../../../../graphql/mutation/product/batchUpdateInventoryProductMutation';
import useNotification from '../../../../../components/_hook/useNotification';
import ButtonSubmit from '../../../../../components/_button/ButtonSubmit';
import useForm from '../../../../../components/_hook/useForm';

const useStyles = makeStyles<Theme>((theme) => ({
  paperTabs: {
    flexGrow: 1,
    marginBottom: theme.spacing(2),
  },
}));

interface IProps {
  batchUpdateInventoryProducts: {
    productId: string;
    variationId?: string;
  }[];
  onCompleted: () => void;
  onCancel: () => void;
}

export default function ProductDropshippingInventoryManagementBatchUpdate(
  props: IProps
) {
  interface IFormValue {
    stockInStock?: number;
    stockLeadTime?: number;
  }

  const classes = useStyles();
  const { batchUpdateInventoryProducts, onCancel, onCompleted } = props;
  const { notification } = useNotification();
  const { t } = useTranslation();

  const TABS = {
    IN_STOCK: 'stockInStock',
    LEAD_TIME: 'stockLeadTime',
  };
  const [currentTab, setCurrentTab] = useState(TABS.IN_STOCK);
  const [
    batchUpdateInventoryProductMutation,
    { loading: isBatchUpdatingInventoryProduct },
  ] = useBatchUpdateInventoryProductMutation(
    `updatedProducts {
				_id
				inventory {
                  stock {
                    in_stock
                    reserved
                    lead_time
                  }
                }
				variations { 
                  id
                  attributes
                  inventory {
                    low_stock
                    no_stock
                    in_stock
                    reserved
                    lead_time
                    ability_number
                    ability_time
                    availability
                    date_from
                    date_to
                  }
                }
			}`,
    {
      onCompleted: (data) => {
        if (
          data &&
          data.batchUpdateInventoryProduct &&
          data.batchUpdateInventoryProduct.updatedProducts
        ) {
          notification.success(
            t('__item___updated', {
              item: `${
                data.batchUpdateInventoryProduct.updatedProducts.length
              } ${t('product', {
                count: data.batchUpdateInventoryProduct.updatedProducts.length,
              })}`,
            })
          );
        }
        onCompleted();
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  async function onSubmit(tempValues: IFormValue) {
    const { stockInStock, stockLeadTime } = tempValues;

    let tempStockInStock: number;
    let tempStockLeadTime: number;
    if (currentTab === TABS.IN_STOCK) {
      tempStockInStock = Number(stockInStock);
    }

    if (currentTab === TABS.LEAD_TIME) {
      tempStockLeadTime = Number(stockLeadTime);
    }

    await batchUpdateInventoryProductMutation({
      variables: {
        batchUpdateInventoryProducts: batchUpdateInventoryProducts.map(
          (batchUpdateInventoryProduct) => ({
            productId: batchUpdateInventoryProduct.productId,
            variationId: batchUpdateInventoryProduct.variationId,
            stockInStock: tempStockInStock,
            stockLeadTime: tempStockLeadTime,
          })
        ),
      },
    });
  }

  function onValidate(tempValues: IFormValue) {
    const newErrors: Record<string, string> = {};

    if (currentTab === TABS.IN_STOCK) {
      if (!tempValues.stockInStock) {
        newErrors.stockInStock = t('__names__are_required', {
          names: t('in_stock'),
        });
      }
    }

    if (currentTab === TABS.LEAD_TIME) {
      if (!tempValues.stockLeadTime) {
        newErrors.stockLeadTime = t('__names__are_required', {
          names: t('lead_time (day)'),
        });
      }
    }

    return newErrors;
  }

  const {
    values,
    handleSubmit,
    errors,
    handleChange,
    handleValidation,
  } = useForm<IFormValue>({
    defaultValues: {
      stockInStock: undefined,
      stockLeadTime: undefined,
    },
    onSubmit,
    onValidate,
  });

  return (
    <>
      <DialogTitle style={{ textTransform: 'capitalize' }}>
        {t('batch_update')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} justifyContent="center">
          <Grid container justifyContent="center" direction="row" xs={10}>
            <Paper className={classes.paperTabs}>
              <Tabs
                variant="fullWidth"
                centered
                value={currentTab}
                indicatorColor="primary"
                textColor="primary"
                onChange={(event, newValue) => setCurrentTab(newValue)}
              >
                <Tab label={t('in_stock')} value={TABS.IN_STOCK} />
                <Tab label={t('lead_time (day)')} value={TABS.LEAD_TIME} />
              </Tabs>
            </Paper>
            <Fade in={currentTab === TABS.IN_STOCK}>
              <Grid
                container
                spacing={2}
                item
                xs={12}
                style={{
                  display: currentTab === TABS.IN_STOCK ? 'inherit' : 'none',
                }}
              >
                <Grid item xs={12}>
                  <TextField
                    type="number"
                    fullWidth
                    required
                    name="stockInStock"
                    label={t('in_stock')}
                    value={values.stockInStock}
                    onChange={handleChange}
                    error={Boolean(errors.stockInStock)}
                    helperText={errors.stockInStock}
                    onBlur={handleValidation}
                  />
                </Grid>
              </Grid>
            </Fade>
            <Fade in={currentTab === TABS.LEAD_TIME}>
              <Grid
                container
                spacing={2}
                item
                xs={12}
                style={{
                  display: currentTab === TABS.LEAD_TIME ? 'inherit' : 'none',
                }}
              >
                <Grid item xs={12}>
                  <TextField
                    type="number"
                    fullWidth
                    required
                    name="stockLeadTime"
                    label={t('lead_time (day)')}
                    value={values.stockLeadTime}
                    onChange={handleChange}
                    error={Boolean(errors.stockLeadTime)}
                    helperText={errors.stockLeadTime}
                    onBlur={handleValidation}
                  />
                </Grid>
              </Grid>
            </Fade>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item>
            <Button variant="text" onClick={onCancel}>
              {t('cancel')}
            </Button>
          </Grid>
          <Grid item>
            <ButtonSubmit
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              isSubmitting={isBatchUpdatingInventoryProduct}
              label={t('submit')}
              icon={<IconsSend />}
            />
          </Grid>
        </Grid>
      </DialogActions>
    </>
  );
}
